/**
 * UploadStep Component
 * Document upload step in the import wizard
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import { UploadZone } from '@/components/documents/UploadZone';
import { UploadProgress } from '@/components/documents/UploadProgress';
import { UpgradePrompt } from '@/components/billing';
import { documentsService } from '@/services/documents';
import { useSubscription } from '@/hooks/useBilling';
import { ApiClientError } from '@/services/api';
import { getUploadMimeType } from '@/utils/uploadMimeType';
import {
  SUPPORTED_EXTENSIONS,
  FILE_SIZE_LIMITS,
} from '@/types/document';

/**
 * Local storage key for PDF consent
 * When user consents, we store this to avoid asking again
 */
const PDF_CONSENT_STORAGE_KEY = 'flowio_pdf_gemini_consent';

export interface UploadStepProps {
  /** Callback when upload completes successfully */
  onComplete: (stagedImportId: string) => void;
  /** Callback when user cancels */
  onCancel: () => void;
}

type UploadState = 'idle' | 'initializing' | 'uploading' | 'processing';
type ErrorType = 'validation' | 'upload' | 'size_exceeded' | 'limit_reached';

interface UploadError {
  type: ErrorType;
  message: string;
  showUpgrade?: boolean;
}

/**
 * Validate file before upload
 */
function validateFile(file: File, maxSizeMb: number): UploadError | null {
  // Check file extension
  const extension = '.' + file.name.split('.').pop()?.toLowerCase();
  const supportedExtensions = SUPPORTED_EXTENSIONS as readonly string[];

  if (!supportedExtensions.includes(extension)) {
    const displayTypes = supportedExtensions
      .slice(0, 8) // Show first 8 for brevity
      .map((ext) => ext.replace('.', '').toUpperCase())
      .join(', ');
    return {
      type: 'validation',
      message: `File type not supported. Please upload: ${displayTypes}, etc.`,
    };
  }

  // Check file size
  const sizeMb = file.size / (1024 * 1024);
  if (sizeMb > maxSizeMb) {
    return {
      type: 'size_exceeded',
      message: `File too large (${sizeMb.toFixed(1)} MB). Maximum size: ${maxSizeMb} MB.`,
      showUpgrade: maxSizeMb === FILE_SIZE_LIMITS.free,
    };
  }

  return null;
}

/**
 * Upload step component for document import wizard.
 * Handles file selection, validation, and 3-step pre-signed URL upload.
 */
export default function UploadStep({ onComplete, onCancel }: UploadStepProps) {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<UploadError | null>(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // PDF consent state - check localStorage for previous consent
  const [hasPdfConsent, setHasPdfConsent] = useState<boolean>(() => {
    try {
      return localStorage.getItem(PDF_CONSENT_STORAGE_KEY) === 'true';
    } catch {
      // localStorage may not be available (e.g., private browsing)
      return false;
    }
  });

  // Persist consent to localStorage when it changes
  useEffect(() => {
    if (hasPdfConsent) {
      try {
        localStorage.setItem(PDF_CONSENT_STORAGE_KEY, 'true');
      } catch {
        // Ignore localStorage errors
      }
    }
  }, [hasPdfConsent]);

  // Get subscription to determine file size limit
  const { data: subscription } = useSubscription();
  const isPremium = subscription?.hasActiveSubscription ?? false;
  const maxSizeMb = isPremium ? FILE_SIZE_LIMITS.premium : FILE_SIZE_LIMITS.free;

  /**
   * Handle file selection from upload zone
   */
  const handleFileSelect = useCallback(
    async (selectedFile: File) => {
      // Validate file
      const validationError = validateFile(selectedFile, maxSizeMb);
      if (validationError) {
        setError(validationError);
        return;
      }

      setFile(selectedFile);
      setError(null);
      setUploadState('initializing');
      setProgress(null);

      // Create abort controller for cancellation
      abortControllerRef.current = new AbortController();

      try {
        // Step 1: Initialize upload and get pre-signed URL
        const initResponse = await documentsService.initUpload({
          filename: selectedFile.name,
          mimeType: getUploadMimeType(selectedFile),
          sizeBytes: selectedFile.size,
        });

        // Check if cancelled
        if (abortControllerRef.current?.signal.aborted) {
          return;
        }

        setUploadState('uploading');
        setProgress(0);

        // Step 2: Upload directly to S3
        const etag = await documentsService.uploadToS3(
          initResponse.uploadUrl,
          initResponse.uploadFields,
          selectedFile,
          (uploadProgress) => {
            setProgress(uploadProgress);
          }
        );

        // Check if cancelled
        if (abortControllerRef.current?.signal.aborted) {
          return;
        }

        setUploadState('processing');
        setProgress(null);

        // Step 3: Complete upload and trigger processing
        const completeResponse = await documentsService.completeUpload({
          documentId: initResponse.documentId,
          etag,
        });

        // Navigate to next step
        // WP-BUG-19: Always navigate with document ID, let DocumentImportWizard handle polling
        // The staged import may not be ready yet if processing is async
        if (completeResponse.stagedImportId) {
          // Staged import is ready immediately - use it directly
          onComplete(`id:${completeResponse.stagedImportId}`);
        } else {
          // Processing is async - pass document ID for polling
          onComplete(`documentId:${completeResponse.document.id}`);
        }
      } catch (err) {
        // Ignore errors from cancelled uploads
        if (abortControllerRef.current?.signal.aborted) {
          return;
        }

        // Check for 402 Payment Required (upload limit reached)
        if (err instanceof ApiClientError && err.status === 402) {
          setShowUpgradePrompt(true);
          setUploadState('idle');
          setProgress(null);
          return;
        }

        // Provide more specific error messages based on the error
        let errorMessage = 'Upload failed. Please try again.';
        let errorType: ErrorType = 'upload';

        if (err instanceof ApiClientError) {
          // Handle API errors with structured response
          if (err.status === 402) {
            // Double-check for 402 (handled above, but be safe)
            setShowUpgradePrompt(true);
            setUploadState('idle');
            setProgress(null);
            return;
          } else if (err.status === 404) {
            errorMessage = 'Upload service unavailable. Please try again later.';
          } else if (err.status === 413) {
            errorMessage = 'File is too large for upload. Please try a smaller file.';
            errorType = 'size_exceeded';
          } else {
            errorMessage = err.detail || err.message;
          }
        } else if (err instanceof Error) {
          // Check for common error patterns
          if (err.message.includes('network') || err.message.includes('Network')) {
            errorMessage = 'Network error during upload. Please check your connection and try again.';
          } else if (err.message.includes('CORS') || err.message.includes('cors')) {
            errorMessage = 'Upload configuration error. Please try again or contact support.';
          } else if (err.message.includes('403') || err.message.includes('Forbidden')) {
            errorMessage = 'Upload permission expired. Please try again.';
          } else if (err.message.includes('413') || err.message.includes('too large')) {
            errorMessage = 'File is too large for upload. Please try a smaller file.';
            errorType = 'size_exceeded';
          } else if (err.message.includes('402') || err.message.includes('Payment Required')) {
            // Fallback check for 402 in error message
            setShowUpgradePrompt(true);
            setUploadState('idle');
            setProgress(null);
            return;
          } else if ('status' in err && (err as { status: number }).status === 404) {
            errorMessage = 'Upload service unavailable. Please try again later.';
          } else {
            errorMessage = err.message;
          }
        }

        setError({
          type: errorType,
          message: errorMessage,
        });
        setUploadState('idle');
        setProgress(null);
      }
    },
    [maxSizeMb, onComplete]
  );

  /**
   * Handle upload cancellation
   */
  const handleCancelUpload = useCallback(() => {
    abortControllerRef.current?.abort();
    setUploadState('idle');
    setFile(null);
    setProgress(null);
    setError(null);
  }, []);

  /**
   * Handle retry after error
   */
  const handleRetry = useCallback(() => {
    setError(null);
    setFile(null);
    setUploadState('idle');
    setProgress(null);
  }, []);

  /**
   * Navigate to upgrade page
   */
  const handleUpgrade = useCallback(() => {
    navigate('/billing');
  }, [navigate]);

  /**
   * Handle closing the upgrade prompt
   */
  const handleCloseUpgradePrompt = useCallback(() => {
    setShowUpgradePrompt(false);
    setFile(null);
  }, []);

  const isUploading = uploadState !== 'idle';

  return (
    <>
    <Card>
      {/* Error state */}
      {error && (
        <UploadError
          error={error}
          onRetry={handleRetry}
          onUpgrade={error.showUpgrade ? handleUpgrade : undefined}
        />
      )}

      {/* Upload progress */}
      {!error && isUploading && file && (
        <UploadProgress
          filename={file.name}
          progress={progress}
          stage={uploadState === 'initializing' ? 'initializing' : uploadState === 'processing' ? 'processing' : 'uploading'}
          canCancel={uploadState === 'uploading'}
          onCancel={handleCancelUpload}
        />
      )}

      {/* PDF consent acknowledgment - shown before upload zone when consent not given */}
      {!error && !isUploading && !hasPdfConsent && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <h3 className="text-sm font-semibold text-amber-900 mb-2">
            Before You Upload
          </h3>
          <p className="text-sm text-amber-800 mb-3">
            To accurately extract transaction data from your documents, PDF files
            are processed using Google Gemini AI. This means your document content
            (including account numbers and transaction details) will be sent to
            Google&apos;s servers for processing.
          </p>
          <ul className="text-sm text-amber-800 mb-4 list-disc list-inside space-y-1">
            <li>Your data is processed in real-time and not stored by Google</li>
            <li>Processing is subject to Google&apos;s Enterprise Data Terms</li>
            <li>CSV and OFX files are processed locally without external services</li>
          </ul>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={hasPdfConsent}
              onChange={(e) => setHasPdfConsent(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-amber-400 text-primary-600 focus:ring-primary-500"
              aria-describedby="pdf-consent-description"
            />
            <span id="pdf-consent-description" className="text-sm text-amber-900">
              I understand and agree to process my PDF documents with Google Gemini AI
            </span>
          </label>
        </div>
      )}

      {/* Upload zone (idle state) */}
      {!error && !isUploading && (
        <UploadZone
          onFileSelect={handleFileSelect}
          maxSizeMb={maxSizeMb}
          supportedTypes={SUPPORTED_EXTENSIONS}
          disabled={isUploading}
          requiresPdfConsent={!hasPdfConsent}
        />
      )}

      {/* Cancel button (only in idle state) */}
      {!isUploading && !error && (
        <div className="mt-4 flex justify-start">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      )}
    </Card>

    {/* Upgrade prompt modal for 402 Payment Required */}
    <UpgradePrompt
      isOpen={showUpgradePrompt}
      onClose={handleCloseUpgradePrompt}
      title="Upload Limit Reached"
      message="You've reached the free tier limit of 10 uploads. Upgrade to Pro for unlimited document uploads."
      limitReached="10 uploads"
    />
    </>
  );
}

// ============================================================================
// Error Display Component
// ============================================================================

interface UploadErrorProps {
  error: UploadError;
  onRetry: () => void;
  onUpgrade?: () => void;
}

/**
 * Headline per error type. It used to read "File type not supported" for every
 * error except size, so a dropped connection or a server error told the user
 * their file type was wrong.
 */
const ERROR_TITLES: Record<ErrorType, string> = {
  validation: 'File type not supported',
  size_exceeded: 'File too large',
  limit_reached: 'Upload limit reached',
  upload: 'Upload failed',
};

function UploadError({ error, onRetry, onUpgrade }: UploadErrorProps) {
  const isFileSizeError = error.type === 'size_exceeded';

  return (
    <div
      className="border-2 border-dashed border-red-300 rounded-lg p-8 sm:p-12 text-center bg-red-50"
      role="alert"
    >
      <ErrorIcon className="mx-auto h-12 w-12 text-red-400" />

      <p className="mt-4 text-lg font-medium text-gray-900">
        {ERROR_TITLES[error.type]}
      </p>

      <p className="mt-2 text-sm text-gray-600">{error.message}</p>

      <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
        {onUpgrade && (
          <Button variant="primary" onClick={onUpgrade}>
            Upgrade to Premium
          </Button>
        )}
        <Button variant={onUpgrade ? 'outline' : 'primary'} onClick={onRetry}>
          {isFileSizeError ? 'Choose Another File' : 'Try Again'}
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Icon Components
// ============================================================================

function ErrorIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
      />
    </svg>
  );
}
